import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';

import QuestionsListAssetTreeSummary from '@/components/atom/QuestionsListAssetTreeSummary/QuestionsListAssetTreeSummary';
import QuestionsListItems from '@/components/atom/QuestionsListItems/QuestionsListItems';
import { useChatComposerFocus } from '@/components/molecule/ChatInput/useChatComposerFocus';
import { THandleSendParams } from '@/components/pages/ChatPage/types';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { addQuestions, selectAllFAQs } from '@/store/faq/faqSlice';
import { TFAQsItem } from '@/store/faq/types';
import { getFaqIconStats, getMappedFaqs } from '@/store/faq/utils';
import { setIsSearching } from '@/store/search/searchSlice';
import { closeSidebar } from '@/store/sidebar/sidebarSlice';
import { selectActiveThreadId, selectIsThreadLoading } from '@/store/thread/threadSlice';

type ExampleQuestionsFilterListProps = {
  isLoading?: boolean;
  isOnboarding: boolean;
  handleSend?: (params: THandleSendParams) => Promise<void>;
  isPanelOpen: boolean;
  onClosePanel: () => void;
};

const ExampleQuestionsFilterList = ({
  isLoading,
  isOnboarding,
  handleSend,
  isPanelOpen,
  onClosePanel,
}: ExampleQuestionsFilterListProps) => {
  const dispatch = useAppDispatch();
  const { focusMessageInput } = useChatComposerFocus();

  const activeThreadId = useAppSelector(selectActiveThreadId);
  const isThreadsLoading = useAppSelector(selectIsThreadLoading(activeThreadId));
  const allFAQs = useAppSelector(selectAllFAQs);

  const intl = useIntl();
  const mappedFAQs = useMemo(() => getMappedFaqs(intl), []); // INFO: inlt can be missing as dependency because it won't change at runtime without the whole page re-mounting
  const faqIcons = useMemo(() => getFaqIconStats(mappedFAQs), [mappedFAQs]);

  useEffect(() => {
    // INFO: Populate the FAQ store with mapped questions on mount
    dispatch(addQuestions(mappedFAQs));
  }, [mappedFAQs]);

  const isSmallScreen = useMediaQuery({ query: '(max-width: 47.5rem)' });

  const [searchValue, setSearchValue] = useState('');
  const [filterFaqsList, setFilterFaqsList] = useState<string | null>(null);

  // INFO: Derived filtered FAQ list from the store
  const faqsList = useMemo(() => {
    return (allFAQs ?? []).filter(
      (item) =>
        item.name.toLowerCase().includes(searchValue.toLowerCase()) &&
        (filterFaqsList ? item.icon === filterFaqsList : true),
    );
    // INFO: allFAQs is the source data, searchValue the search term, filterFaqsList the active category filter
  }, [allFAQs, searchValue, filterFaqsList]);

  // INFO: Reset state when panel closes
  useEffect(() => {
    if (!isPanelOpen) {
      setSearchValue('');
      setFilterFaqsList(null);
      dispatch(setIsSearching(false));
    }
  }, [isPanelOpen, dispatch]);

  const handleSelection = async (item: TFAQsItem) => {
    if (isOnboarding && handleSend) {
      await handleSend({
        content: item.name,
        isOnboarding,
        nextStepIndex: 'step-0',
      });
    }

    const question = mappedFAQs.find((q) => q.id === item.id);

    if (isLoading || isThreadsLoading || !question) return;

    if (handleSend) {
      await handleSend({
        content: question.name,
        isOnboarding: false,
      });
      dispatch(setIsSearching(false));
      onClosePanel();
      focusMessageInput();
    }

    if (isSmallScreen) {
      dispatch(closeSidebar());
    }
  };

  return (
    <>
      {!isThreadsLoading && (
        <>
          <QuestionsListAssetTreeSummary
            isOnboarding={isOnboarding}
            faqIcons={faqIcons}
            activeIcon={filterFaqsList}
            onFilterSelect={setFilterFaqsList}
            onSearch={setSearchValue}
          />
          <QuestionsListItems
            isOnboarding={isOnboarding}
            faqsList={faqsList}
            searchValue={searchValue}
            handleSelection={handleSelection}
          />
        </>
      )}
    </>
  );
};

export default ExampleQuestionsFilterList;
